import { useTranslation } from 'react-i18next';
import { useUserRoles } from '@anytag/auth';
import { Anchor } from '@anytag/shared-atoms';
import { cn, formatIntNumber } from '@anytag/shared-utils';
import { InfluencerAvatar } from '@anytag/shared-molecules';
import { useGenerateInfluencerProfileLink } from '@anytag/shared-hooks';
import { PostDetailInfluencerInfo, PostDetailsType, SupportedSocialMediaType } from '@anytag/shared-types';

export interface InfluencerInfoProps extends Pick<PostDetailsType, 'socialMedia'> {
  className?: string;
  postStatus?: React.ReactNode;
  info: PostDetailInfluencerInfo;
  objectFit?: 'contain' | 'cover';
}

export const InfluencerInfo = ({
  className,
  postStatus,
  socialMedia,
  objectFit = 'contain',
  info: { id, name, avatar, followers }
}: InfluencerInfoProps) => {
  const { t } = useTranslation();
  const { isAdvertiser } = useUserRoles();
  const { generateProfileLink } = useGenerateInfluencerProfileLink({ disabled: isAdvertiser || !id });

  const hasFollowers = typeof followers !== 'undefined';
  const profileLink = generateProfileLink(id);
  const isFbPageOrYoutube = [SupportedSocialMediaType.FACEBOOK_PAGE, SupportedSocialMediaType.YOUTUBE].includes(
    socialMedia as SupportedSocialMediaType
  );

  return (
    <div className={cn('flex flex-none items-center gap-1 text-black-90', className)}>
      <InfluencerAvatar src={avatar} size="40px" objectFit={objectFit} className="rounded-full" id={id} />

      <div>
        <Anchor label={name} className="text-lg leading-6 font-semibold" {...profileLink} />

        {hasFollowers ? (
          <p className="mt-0.5 text-xs text-black-60">
            {formatIntNumber(followers)}&nbsp;
            {t(isFbPageOrYoutube ? 'Subscribers' : 'Followers')}
          </p>
        ) : null}
      </div>

      {postStatus ? <div className="ml-auto w-max">{postStatus}</div> : null}
    </div>
  );
};
